import { Directive, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { createValidUrlParameter } from '../../shared/utils/create-valid-url-parameter.util';
import { UrlService } from '../state/url.service';

/**
 * Basis-Klasse für alle Services, die die ausgewählten Optionen einer Facette in der URL hinterlegen.
 * Es werden Methoden bereitgestellt zum Speichern der Parameter der jeweiligen Facette und
 * zu deren reaktiven Abrufen.
 */
// Directive-Annotation als Workaround, um den Angular-Compiler zu besänftigen.
// Dieser erlaubt nämlich ngOnDestroy nur in Angular-Klasse.
@Directive()
export abstract class FacettenUrlService implements OnDestroy {
  private readonly subscription: Subscription;

  public urlService: UrlService = inject(UrlService);

  public urlParam: string;
  public backendParam: string;

  protected constructor(urlParam: string, backendParam: string) {
    this.urlParam = urlParam;
    this.backendParam = backendParam;

    this.subscription = this.urlService.getQueryParameterValues(urlParam).subscribe((value) => {
      this.selectedOptions$.next(value);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected selectedOptions$: BehaviorSubject<string[]> = new BehaviorSubject([] as string[]);

  /**
   * Ids der ausgewählten Optionen der Facette
   */
  public get selectedOptionIds$(): Observable<string[]> {
    return this.selectedOptions$;
  }

  /**
   * Überschreibt die selektierten Optionen der Facette
   */
  public async updateSelection(facetteOptionIds: string[]): Promise<void> {
    await this.urlService.updateView({
      [this.urlParam]: createValidUrlParameter(facetteOptionIds),
      [UrlService.SELECTED_ANGEBOT]: null,
      [UrlService.AKTUELLE_SEITE]: null
    });
  }

  /**
   * Wechselt den Zustand einer Option von selektiert auf nicht-selektiert und umgekehrt.
   *
   * @param facetteOptionId ID der Facetten-Option
   * @param wasSelected Ehemaliger Status der Selektion vor dem auslösenden Event
   */
  public async toggleOptionSelection(facetteOptionId: string, wasSelected: boolean): Promise<void> {
    let parameter;
    if (wasSelected) {
      parameter = this.selectedOptions$.value.filter((s) => s !== facetteOptionId);
    } else {
      parameter = this.selectedOptions$.value.concat(facetteOptionId);
    }
    await this.updateSelection(parameter);
  }

  /**
   * Setzt alle die gesamte Auswahl für diese Facette zurück.
   */
  public async resetOptionSelections(): Promise<void> {
    await this.updateSelection([]);
  }

  /**
   * Flag, das anzeigt, dass mindestens eine Option ausgewählt wurde, welche man zurücksetzen kann.
   */
  public showReset$: Observable<boolean> = this.selectedOptionIds$.pipe(
    map((items) => items.length > 0)
  );
}
